import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getCourseById, getModules, updateCourse, getCourseFullDetails, getCourseEnrollments, deleteCourse } from "../api";
import ExportModal from "./exportmodal";
import "./ViewCourses.css";

const ViewCourse = () => {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showEnrollmentsModal, setShowEnrollmentsModal] = useState(false);
  const [enrolledLearners, setEnrolledLearners] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [enrollmentFilter, setEnrollmentFilter] = useState('all'); // 'all', 'active', 'completed'

  const normalizeModules = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.modules)) return raw.modules;
    if (Array.isArray(raw?.data?.modules)) return raw.data.modules;
    return [];
  };

  useEffect(() => {
  const fetchCourseData = async () => {
    try {
      setLoading(true);
      setError(null);

      // get full details in one go
      const fullData = await getCourseFullDetails(id);
      setCourse(fullData.course || fullData);
      setModules(fullData.modules || fullData.modules_data || []);
    } catch (err) {
      console.error("Error fetching full course details:", err);
      setError("Failed to load course details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  fetchCourseData();
}, [id]);


  const handleChangeStatus = async (status) => {
    if (!course) return;

    // Confirmation
    if (!window.confirm(`Are you sure you want to set this course as "${status}"?`)) return;

    try {
      setUpdatingStatus(true);
      let payload = {};
      if (status === "public") payload = { is_published: true, is_archived: false };
      else if (status === "draft") payload = { is_published: false, is_archived: false };
      else if (status === "archive") payload = { is_archived: true };

      await updateCourse(course.id, payload);
      setCourse(prev => ({ ...prev, ...payload }));
      setToastMessage(`Course status updated to "${status}"!`);
      setTimeout(() => setToastMessage(""), 3000); // auto hide toast
    } catch (err) {
      console.error("Failed to update course status:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = () => {
    if (course.is_archived) return "red";
    if (course.is_published) return "green";
    return "orange";
  };

  const handleEnrollmentsClick = async () => {
    if (!course || !course.id) return;

    setShowEnrollmentsModal(true);
    setLoadingEnrollments(true);
    setEnrollmentFilter('all'); // Reset filter when opening modal

    try {
      const enrollments = await getCourseEnrollments(course.id);
      setEnrolledLearners(enrollments);
    } catch (err) {
      console.error("Error fetching enrollments:", err);
      setEnrolledLearners([]);
    } finally {
      setLoadingEnrollments(false);
    }
  };

  // Filter enrollments based on selected filter
  const getFilteredEnrollments = () => {
    if (enrollmentFilter === 'all') {
      return enrolledLearners;
    } else if (enrollmentFilter === 'active') {
      return enrolledLearners.filter(e => {
        // Active: not completed status, no completion date, and progress less than 100
        const isCompleted = e.status === 'completed' ||
                           (e.completed_at != null && e.completed_at !== '') ||
                           e.progress === 100 ||
                           e.progress_percentage === 100;
        return !isCompleted;
      });
    } else if (enrollmentFilter === 'completed') {
      return enrolledLearners.filter(e => {
        // Completed: has completed status OR has completion date OR progress is 100%
        return e.status === 'completed' ||
               (e.completed_at != null && e.completed_at !== '') ||
               e.progress === 100 ||
               e.progress_percentage === 100;
      });
    }
    return enrolledLearners;
  };

  const filteredEnrollments = getFilteredEnrollments();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getLearnerName = (staff) => {
    if (!staff) return 'Unknown Learner';
    if (staff.full_name) return staff.full_name;
    if (staff.firstname || staff.lastname) {
      return `${staff.firstname || ''} ${staff.lastname || ''}`.trim();
    }
    return staff.email || staff.phone_number || 'Unknown Learner';
  };

  // Extract cadres/job titles from course data
  const getCourseCadres = (course) => {
    if (!course) return [];
    const cadres = course.cadres || course.job_titles || course.jobTitles || [];
    if (!Array.isArray(cadres)) return [];
    
    return cadres.map(cadre => {
      if (typeof cadre === 'string') return cadre;
      if (typeof cadre === 'object' && cadre !== null) {
        return cadre.name || cadre.title || cadre.cadre || 'Unknown';
      }
      return 'Unknown';
    }).filter(c => c && c !== 'Unknown');
  };

  if (loading) return <p className="loading-message">Loading course details...</p>;
  if (error) return <p className="error-message">{error}</p>;
  if (!course) return <p className="error-message">Course not found.</p>;

  return (
    <div className="view-course-page">

      {/* Toast Notification */}
      {toastMessage && <div className="toast">{toastMessage}</div>}

      {/* Course Header */}
      <div className="course-header-card">
        <img
          src={course.image_url || "/fallback.jpg"}
          alt={course.title}
          className="course-thumbnail"
          onError={e => e.target.src = "/fallback.jpg"}
        />
        <div className="course-info">
          <h1>{course.title}</h1>
          <p className="course-description">{course.description || "No description provided."}</p>
          {getCourseCadres(course).length > 0 && (
            <div className="course-cadres-section">
              <div className="cadres-header">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                <span className="cadres-title">Target Cadres</span>
              </div>
              <div className="cadres-container">
                {getCourseCadres(course).map((cadre, idx) => (
                  <div key={idx} className="cadre-card">
                    <span className="cadre-name">{cadre}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="course-actions-bar">
        <Link
          to={`/courses/edit/${course.id}`}
          className="btn-edit-course"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          Edit Course
        </Link>
        <button
          className="btn-delete-course"
          onClick={async () => {
            if (window.confirm(`Are you sure you want to delete "${course.title}"? This action cannot be undone and will delete all related data (modules, topics, contents, enrollments, etc.).`)) {
              try {
                await deleteCourse(course.id);
                setToastMessage('Course deleted successfully!');
                setTimeout(() => {
                  window.location.href = '/courses';
                }, 1500);
              } catch (err) {
                console.error('Failed to delete course:', err);
                alert(err?.message || 'Failed to delete course. Please try again.');
              }
            }
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
          Delete Course
        </button>
      </div>

      {/* Status Buttons */}
      {/* Status Buttons */}
<div className="status-buttons">
  <button
    className={`btn-status ${course.is_published && !course.is_archived ? "active" : "inactive green"}`}
    onClick={() => handleChangeStatus("public")}
    disabled={updatingStatus}
  >
    Public
  </button>
  <button
    className={`btn-status ${!course.is_published && !course.is_archived ? "active" : "inactive orange"}`}
    onClick={() => handleChangeStatus("draft")}
    disabled={updatingStatus}
  >
    Draft
  </button>
  <button
    className={`btn-status ${course.is_archived ? "active" : "inactive red"}`}
    onClick={() => handleChangeStatus("archive")}
    disabled={updatingStatus}
  >
    Archive
  </button>
</div>


      {/* Course Stats */}
      <div className="course-stats-grid">
        <div className="stat-card highlight">
          <div className="stat-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="stat-label">Status</div>
          <div className="stat-value" style={{ color: getStatusColor() }}>
            {course.is_archived
              ? "Archived"
              : course.is_published
              ? "Published"
              : "Draft"}
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2h-8l-2-2z"/>
            </svg>
          </div>
          <div className="stat-label">Category</div>
          <div className="stat-value">{course.category?.name || "N/A"}</div>
        </div>

        <div 
          className="stat-card highlight clickable" 
          onClick={handleEnrollmentsClick}
          style={{ cursor: 'pointer' }}
          title="Click to view enrolled learners"
        >
          <div className="stat-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          <div className="stat-label">Enrollments</div>
          <div className="stat-value">{course.learners_count || 0}</div>
          <div className="stat-sublabel">
            {course.completed_learners || 0} completed
          </div>
          <div className="stat-click-hint">Click to view →</div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h10v2zm0-8H9V5h10v2z"/>
            </svg>
          </div>
          <div className="stat-label">Modules</div>
          <div className="stat-value">{modules.length || course.modules_count || 0}</div>
          <div className="stat-sublabel">
            {modules.reduce((acc, m) => acc + (m.topics?.length || 0), 0)} topics
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.31-8.86c-1.77-.45-2.34-.94-2.34-1.67 0-.84.79-1.43 2.1-1.43 1.38 0 1.9.66 1.94 1.64h1.71c-.05-1.34-.87-2.57-2.49-2.97V5H10.9v1.69c-1.51.32-2.72 1.3-2.72 2.81 0 1.79 1.49 2.69 3.66 3.21 1.95.46 2.34 1.12 2.34 1.87 0 .53-.39 1.39-2.1 1.39-1.6 0-2.23-.72-2.32-1.64H8.04c.1 1.7 1.36 2.66 2.86 2.97V19h2.34v-1.67c1.52-.29 2.72-1.16 2.73-2.77-.01-2.2-1.9-2.96-3.66-3.42z"/>
            </svg>
          </div>
          <div className="stat-label">Est. Hours</div>
          <div className="stat-value">{modules.length || course.modules_count || 'N/A'}</div>
          <div className="stat-sublabel">Estimated</div>
        </div>

        {course.is_published && (
          <div className="stat-card highlight">
            <div className="stat-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
              </svg>
            </div>
            <div className="stat-label">Published</div>
            <div className="stat-value">
              {course.published_at 
                ? new Date(course.published_at).toLocaleDateString('en-GB', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric' 
                  })
                : course.updated_at && course.is_published
                ? new Date(course.updated_at).toLocaleDateString('en-GB', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric' 
                  })
                : "N/A"}
            </div>
          </div>
        )}

        <div className="stat-card highlight">
          <div className="stat-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
            </svg>
          </div>
          <div className="stat-label">Created</div>
          <div className="stat-value">
            {course.created_at 
              ? new Date(course.created_at).toLocaleDateString('en-GB', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric' 
                })
              : "N/A"}
          </div>
        </div>

        {course.tests_count > 0 && (
          <div className="stat-card">
            <div className="stat-icon-wrapper">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
            </div>
            <div className="stat-label">Tests</div>
            <div className="stat-value">{course.tests_count || 0}</div>
            {course.tests_avg_percentile && (
              <div className="stat-sublabel">
                Avg: {Math.round(course.tests_avg_percentile)}%
              </div>
            )}
          </div>
        )}
      </div>
              
      {/* Modules Section */}
<div className="modules-section">
  <h2 className="section-title">Modules</h2>
  {modules.length === 0 ? (
    <p>No modules available for this course.</p>
  ) : (
    <div className="modules-grid">
      {modules.map((module, idx) => (
        <div key={module.id ?? idx} className="module-card">
          <h3>{module.title || `Module ${idx + 1}`}</h3>
          <p>{module.description || "No description provided."}</p>

           {/* {{Array.isArray(module.topics) && module.topics.length > 0 ? (
            <div className="topics-list">
              {module.topics.map((topic, tIdx) => (
                <div key={topic.id ?? tIdx} className="topic-card">
                  <h4>{topic.title || `Topic ${tIdx + 1}`}</h4>
                  <p>{topic.description || "No description provided."}</p>

                  
                  {Array.isArray(topic.contents) && topic.contents.length > 0 ? (
                    <ul className="content-list">
                      {topic.contents.map((content, cIdx) => (
                        <li key={content.id ?? cIdx}>
                          <strong>{content.title}</strong> ({content.type})
                          {content.type === "video" && (
                            <video
                              src={content.url}
                              controls
                              width="100%"
                              style={{ borderRadius: "8px", marginTop: "5px" }}
                            />
                          )}
                          {content.type === "document" && (
                            <a
                              href={content.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-link"
                            >
                              View Document
                            </a>
                          )}
                          {content.type === "audio" && (
                            <audio
                              controls
                              src={content.url}
                              style={{ marginTop: "5px", width: "100%" }}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No contents available for this topic.</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p>No topics available in this module.</p>
          )} } */}
        </div>
      ))}
    </div>
  )}
</div>

      {/* Back Button */}
      <div className="back-button">
        <Link to="/courses" className="btn-primary">← Back to Courses</Link>
      </div>

      {/* Enrollments Modal */}
      {showEnrollmentsModal && (
        <div className="enrollments-modal-overlay" onClick={() => setShowEnrollmentsModal(false)}>
          <div className="enrollments-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enrollments-modal-header">
              <div className="enrollments-header-content">
                <h2>Enrolled Learners</h2>
                {enrolledLearners.length > 0 && (
                  <button 
                    onClick={() => {
                      setShowEnrollmentsModal(false);
                      setShowExportModal(true);
                    }}
                    className="btn-export-enrollments"
                    title="Export enrollments"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13v6H5v-6H3v8h18v-8h-2zm-6-10h-2v8.586L6.707 7.293 5.293 8.707 12 15.414l6.707-6.707-1.414-1.414L13 11.586V3z"/>
                    </svg>
                    Export
                  </button>
                )}
              </div>
              <button 
                className="enrollments-modal-close" 
                onClick={() => setShowEnrollmentsModal(false)}
                aria-label="Close modal"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div className="enrollments-modal-body">
              <div className="enrollments-modal-info">
                <span className="course-name">{course.title}</span>
                <span className="enrollment-count">
                  {enrolledLearners.length} {enrolledLearners.length === 1 ? 'learner' : 'learners'} enrolled
                </span>
              </div>

              {/* Filter Tabs */}
              {!loadingEnrollments && enrolledLearners.length > 0 && (
                <div className="enrollments-filter-tabs" style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '16px',
                  borderBottom: '1px solid #e9ecef',
                  paddingBottom: '8px'
                }}>
                  <button
                    onClick={() => setEnrollmentFilter('all')}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      background: enrollmentFilter === 'all' ? '#F77600' : 'transparent',
                      color: enrollmentFilter === 'all' ? '#fff' : '#6c757d',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: enrollmentFilter === 'all' ? '600' : '400',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    All ({enrolledLearners.length})
                  </button>
                  <button
                    onClick={() => setEnrollmentFilter('active')}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      background: enrollmentFilter === 'active' ? '#F77600' : 'transparent',
                      color: enrollmentFilter === 'active' ? '#fff' : '#6c757d',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: enrollmentFilter === 'active' ? '600' : '400',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Active ({enrolledLearners.filter(e => {
                      const isCompleted = e.status === 'completed' ||
                                         (e.completed_at != null && e.completed_at !== '') ||
                                         e.progress === 100 ||
                                         e.progress_percentage === 100;
                      return !isCompleted;
                    }).length})
                  </button>
                  <button
                    onClick={() => setEnrollmentFilter('completed')}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      background: enrollmentFilter === 'completed' ? '#F77600' : 'transparent',
                      color: enrollmentFilter === 'completed' ? '#fff' : '#6c757d',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: enrollmentFilter === 'completed' ? '600' : '400',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Completed ({enrolledLearners.filter(e =>
                      e.status === 'completed' ||
                      (e.completed_at != null && e.completed_at !== '') ||
                      e.progress === 100 ||
                      e.progress_percentage === 100
                    ).length})
                  </button>
                </div>
              )}

              {loadingEnrollments ? (
                <div className="enrollments-loading">
                  <div className="spinner"></div>
                  <p>Loading enrolled learners...</p>
                </div>
              ) : filteredEnrollments.length === 0 ? (
                <div className="enrollments-empty">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                  <p>
                    {enrollmentFilter === 'all' && 'No learners enrolled in this course yet.'}
                    {enrollmentFilter === 'active' && 'No active learners in this course.'}
                    {enrollmentFilter === 'completed' && 'No learners have completed this course yet.'}
                  </p>
                </div>
              ) : (
                <div className="enrollments-list">
                  {filteredEnrollments.map((enrollment) => (
                    <div key={enrollment.id} className="enrollment-item">
                      <div className="enrollment-item-avatar">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                      </div>
                      <div className="enrollment-item-info">
                        <div className="enrollment-item-name">
                          {getLearnerName(enrollment.staff)}
                        </div>
                        <div className="enrollment-item-details">
                          <span className="enrollment-item-email">
                            {enrollment.staff?.email || enrollment.staff?.phone_number || 'No contact info'}
                          </span>
                          {enrollment.staff?.facility?.name && (
                            <span className="enrollment-item-facility">
                              {enrollment.staff.facility.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="enrollment-item-status">
                        <span className={`status-badge ${enrollment.status || 'active'}`}>
                          {enrollment.status || 'active'}
                        </span>
                        <div className="enrollment-item-date">
                          Enrolled: {formatDate(enrollment.created_at)}
                        </div>
                        {enrollment.status === 'completed' && enrollment.updated_at && (
                          <div className="enrollment-item-date completed">
                            Completed: {formatDate(enrollment.updated_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        context={{
          type: 'enrollments',
          enrollments: enrolledLearners,
          courseName: course?.title || 'Unknown Course',
          courseId: course?.id
        }}
      />

    </div>
  );
};

export default ViewCourse;
